import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Anchor,
  Button,
  Card,
  Divider,
  ActionIcon,
  Flex,
  Grid,
  Space,
  Switch,
  Tabs,
  Text,
  ThemeIcon,
  rem,
  Box,
} from '@mantine/core';
import {
  useParams,
  useSearchParams,
  Route,
  Routes,
  Link,
} from 'react-router-dom';
import { IconForms, IconQrcode, IconExternalLink } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  EntitySubtitles,
  ErrorView,
  MicrositeSettings,
  QrCodeGenerator,
  PageTitle,
  Subnav,
  DataStateCallbackLogs,
} from '@components/shared';
import {
  EffortAddonManagement,
  EffortAlerts,
  EffortAssets,
  EffortAwards,
  EffortCampaignManagement,
  EffortCsvDownload,
  EffortForm,
  EffortLocationJoinList,
  EffortSettings,
  EffortLinks,
  EffortReadOnly,
  EffortStatus,
  EffortWizardStatus,
  MultiSweepsEffortAwards,
  EffortPublicDashboardSettings,
  EffortGenerateDashboardButton,
} from '@components/Effort';
import EffortBuilder from '@components/EffortBuilder';
import { KeywordManagement } from '@components/Keyword';
import { LocationAssociate } from '@components/Location';
import { EntityConfigSettings } from '@components/EntityConfig';
import { GeospotAssignment } from '@components/Geospot';
import { EntityEmailTemplates } from '@components/EmailServer';
import EntityActionAssociator from '@components/EntityActionAssociator';

import { effortRecurringEngagement } from '@components/Effort/helpers';

import entityIcon from '@util/entityIcon';
import entityColor from '@util/entityColor';

import { rideChallengePath } from '@components/RideChallenge/helpers';
import {
  RideChallengeVerificationCodes,
  RideChallengeManagement,
} from '@components/RideChallenge';
import { MilestoneManagement } from '@components/Milestone';
import { getDashboardCode } from '@components/Effort/EffortLocationJoinList';
import {
  EntityMessagingSms,
  BulkCommunicationManagement,
} from '@components/BulkCommunication';
import { UserInteractionTable } from '@components/UserInteraction';

export default function EffortDetail() {
  const queryClient = useQueryClient();
  const [showWizard, setShowWizard] = useState(true);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const { id } = params;
  const queryKey = `efforts${id}`;

  const managerInfo = useSelector((state) => state.manager);
  const adminInfo = useSelector((state) => state.admin);
  const isLocationManager =
    managerInfo && managerInfo.location_id ? true : false;
  const isOrgManager =
    managerInfo && managerInfo.organization_id ? true : false;

  const isManager = managerInfo;

  useEffect(() => {
    // setInfo(null);
    fetchData();
  }, [id]);

  const fetchData = () => queryClient.invalidateQueries([queryKey]);

  const {
    failureReason: error,
    data: info,
    isFetching,
  } = useQuery({
    queryKey: [queryKey],
    queryFn: async () =>
      axios
        .get(`/efforts/${id}/`)
        .then(({ data }) => {
          const res = data.response[0];
          if (
            res.wizard_settings.show_quick_settings === false ||
            JSON.stringify(res.wizard_settings) === '{}' ||
            searchParams.get('quickSettings') === 'false'
          ) {
            setShowWizard(false);
          }
          return res;
        })
        .catch((err) => {
          throw err;
        }),
  });

  // function fetchData() {
  //   axios
  //     .get(`/efforts/${id}/`)
  //     .then(({ data }) => {
  //       setInfo(data.response[0]);
  //       if (
  //         data.response[0].wizard_settings.show_quick_settings === false ||
  //         JSON.stringify(data.response[0].wizard_settings) === "{}" ||
  //         searchParams.get("quickSettings") === "false"
  //       ) {
  //         setShowWizard(false);
  //       }
  //     })
  //     .catch((err) => {
  //       setError(err);
  //       setInfo(null);
  //     });
  // }

  const fetchAlerts = () =>
    queryClient.invalidateQueries([`efforts${id}Alerts`]);

  const fetchCheckupAndAlerts = () =>
    queryClient.invalidateQueries([
      `efforts${id}Alerts`,
      `efforts${id}Checkup`,
    ]);

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  const { wizard_settings: wizardSettings, wizard_status: wizardStatus } = info;
  const wizardComplete = wizardStatus.wizard_complete;
  const hasWizardSettings = JSON.stringify(wizardSettings) !== '{}';
  const isPrizepool = info.variety === 2;
  const isAuction = info.variety === 3;
  const isRideChallenge = info.variety === 6;
  const isVehicleDataEffort = info.variety === 7;

  const showInteraction = adminInfo
    ? true
    : isOrgManager && managerInfo.organization_id === info.organization_id
    ? true
    : false;

  const showKeywords =
    info.recurring_engagement === effortRecurringEngagement.true ||
    (wizardSettings && wizardSettings.show_keywords);

  if (info.edit === false)
    return (
      <React.Fragment>
        <PageTitle
          title={info.title}
          subtitle={
            info.variety === 6
              ? 'Ride Challenge Effort'
              : isVehicleDataEffort
              ? 'Vehicle Data Effort'
              : 'Effort'
          }
          icon={entityIcon.effort()}
          iconColor={entityColor.effort}
        />
        <EffortReadOnly
          effortInfo={{
            ...info,
            id,
          }}
        />
      </React.Fragment>
    );

  if (!wizardComplete || showWizard) {
    return (
      <div>
        <EffortTitle
          info={info}
          id={id}
          fetchData={fetchData}
          fetchCheckupAndAlerts={fetchCheckupAndAlerts}
        />
        {wizardComplete ? (
          <WizardToggle showWizard={showWizard} setShowWizard={setShowWizard} />
        ) : (
          <Space mt="sm" />
        )}
        {wizardComplete && (
          <Box mb="md">
            <EffortAlerts
              effortId={id}
              setShowWizard={(e) => setShowWizard(e)}
            />
          </Box>
        )}
        <EffortWizardStatus
          id={id}
          campaignId={info.campaign_id}
          contestId={info.contest_hash ? info.contest_hash.id : null}
          effortVariety={info.variety}
          effortName={info.title}
          fetchData={fetchData}
          locationId={info.location_id}
          organizationId={info.organization_id}
          pointSettings={info.points_settings}
          status={info.status}
          isRideChallenge={isRideChallenge}
          isVehicleDataEffort={isVehicleDataEffort}
          isRecurring={info.recurring_engagement === 1}
          wizardComplete={wizardComplete}
          wizardSettings={wizardSettings}
          wizardStatus={wizardStatus}
          effortDates={
            info.start_date && info.end_date
              ? {
                  start_date: info.start_date,
                  start_time: info.start_time,
                  start_date_formatted: info.start_date_formatted,
                  end_date: info.end_date,
                  end_date_formatted: info.end_date_formatted,
                  end_time: info.end_time,
                }
              : null
          }
        />
        {wizardComplete && !managerInfo && (
          <Box>
            <EffortSms info={info} fetchData={fetchData} effortId={id} />
          </Box>
        )}
      </div>
    );
  }

  const showVerificationCodes =
    isRideChallenge || isVehicleDataEffort ? true : false;

  const links = [
    {
      text: 'Edit',
      to: `/efforts/${id}`,
    },
    {
      text: 'Builder',
      to: `/efforts/${id}/builder`,
      trigger: 'show_builder',
    },
    {
      text: 'Keywords',
      to: `/efforts/${id}/keywords`,
      trigger: 'show_keywords',
      visible: showKeywords,
    },
    {
      text: 'Awards',
      to: `/efforts/${id}/awards`,
      trigger: 'show_awards',
    },
    {
      text: 'Locations',
      to: `/efforts/${id}/locations`,
      visible: isLocationManager || info.location_id ? false : true,
    },
    {
      text: 'Interaction',
      to: `/efforts/${id}/interaction`,
      visible: showInteraction,
    },
    {
      text: 'Geo-Spots',
      to: `/efforts/${id}/geospots`,
      trigger: 'show_geospots',
      visible: info.location_id ? true : false,
    },
    {
      title: 'Style',
      links: [
        {
          text: 'Assets',
          to: `/efforts/${id}/assets`,
          trigger: 'show_assets',
        },
        {
          text: 'Look & Feel',
          to: `/efforts/${id}/config`,
          trigger: 'show_look_feel',
          visible:
            !isRideChallenge &&
            (wizardSettings.microsite === false ||
              wizardSettings.show_look_feel === false)
              ? false
              : true,
        },
      ]
        .filter((f) => (wizardSettings[f.trigger] === false ? false : true))
        .filter((f) => f.visible !== false),
    },
    {
      title: 'Ride Challenge',
      links: [
        {
          text: 'Verification Codes',
          to: `/efforts/${id}/verification`,
          ride_challenge: true,
          visible: isManager ? false : true,
        },
        {
          text: 'Management',
          to: `/efforts/${id}/ride-challenge-management`,
        },
        {
          text: 'Milestones',
          to: `/efforts/${id}/milestones`,
        },
      ].filter((f) => f.visible !== false),
      visible: isRideChallenge,
    },
    {
      title: 'Messaging',
      links: [
        {
          text: 'SMS Messaging',
          to: `/efforts/${id}/sms-messaging`,
          visible: !isManager,
        },
        // {
        //   text: "Bulk Communication",
        //   to: `/efforts/${id}/bulk-communication`,
        //   visible: !isManager,
        // },
        {
          text: 'Email',
          to: `/efforts/${id}/email`,
          trigger: 'show_email',
          visible: !isManager,
        },
      ]
        .filter((f) => (wizardSettings[f.trigger] === false ? false : true))
        .filter((f) => f.visible !== false),
    },
    {
      text: 'Verification Codes',
      to: `/efforts/${id}/verification`,
      visible: isVehicleDataEffort,
    },
    {
      title: 'Advanced',
      links: [
        {
          text: 'Visible Efforts',
          to: `/efforts/${id}/addons`,
        },
        {
          text: 'Settings',
          to: `/efforts/${id}/settings`,
          visible: wizardSettings.show_settings === false ? false : true,
        },
        {
          text: 'Paid Advertising',
          to: `/efforts/${id}/paid-advertising`,
          visible: wizardSettings?.show_look_feel === true && !isManager,
        },
      ].filter((f) => f.visible !== false),
    },
  ]
    .filter((f) => f.visible !== false)
    .filter((f) => (f.links ? f.links.length > 0 : true))
    .filter((f) => (wizardSettings[f.trigger] === false ? false : true));

  const showMicrosite = !hasWizardSettings
    ? true
    : wizardSettings.microsite === false ||
      wizardSettings.show_look_feel === false
    ? false
    : true;

  const readingGoalDashboardCode = info.reading_goal_dashboard_code;
  const affiliateDashboardCode = info.affiliate_dashboard_code;

  const showStatusButtons =
    isAuction ||
    isPrizepool ||
    (info.variety === 4 && info.registration_method !== 'form');

  return (
    <React.Fragment>
      <EffortTitle
        info={info}
        id={id}
        fetchData={fetchData}
        fetchCheckupAndAlerts={fetchCheckupAndAlerts}
      />
      {wizardComplete &&
        wizardSettings.show_quick_settings !== false &&
        JSON.stringify(wizardSettings) !== '{}' && (
          <WizardToggle showWizard={showWizard} setShowWizard={setShowWizard} />
        )}
      {links.length > 1 ? (
        <Flex mt="sm">
          <Subnav buttonColor={entityColor.effort} links={links} />
        </Flex>
      ) : (
        <Space mt="lg" />
      )}
      {wizardComplete && (
        <Box mb="md">
          <EffortAlerts effortId={id} setShowWizard={(e) => setShowWizard(e)} />
        </Box>
      )}
      <Routes>
        <Route
          exact
          path="/"
          element={
            <Grid>
              <Grid.Col
                span={{
                  base: 12,
                  md:
                    wizardSettings.show_quick_settings !== false ||
                    showStatusButtons
                      ? 9
                      : 12,
                }}
              >
                <Card>
                  <EffortTabs
                    info={info}
                    id={id}
                    fetchData={fetchData}
                    isRideChallenge={isRideChallenge}
                    isVehicleDataEffort={isVehicleDataEffort}
                    readingGoalDashboardCode={readingGoalDashboardCode}
                    affiliateDashboardCode={affiliateDashboardCode}
                    isLocationManager={isLocationManager}
                    showTabList={
                      wizardSettings.microsite ||
                      !hasWizardSettings ||
                      isRideChallenge ||
                      isVehicleDataEffort
                    }
                  />
                </Card>
                {!isManager && (
                  <Card mt="lg">
                    <DataStateCallbackLogs effortId={id} />
                  </Card>
                )}
              </Grid.Col>
              {(wizardSettings.show_quick_settings !== false ||
                showStatusButtons) && (
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <EffortStatus
                    statusOnly={showStatusButtons}
                    effortId={id}
                    fetchData={() => {
                      // fetchData();
                      fetchAlerts();
                    }}
                  />
                </Grid.Col>
              )}
            </Grid>
          }
        />
        <Route
          path="builder"
          element={
            <div>
              <EffortBuilder
                postmarkTemplateNames={info.postmark_template_names}
                effortId={id}
                effortName={info.title}
                entityName={info.organization_name || info.location_name}
                fetchEffortData={() => {
                  fetchCheckupAndAlerts();
                }}
                language={info.language}
              />
            </div>
          }
        />
        <Route
          path="assets"
          element={
            <div>
              <EffortAssets effortId={id} effortName={info.title} />
            </div>
          }
        />
        <Route
          path="keywords"
          element={
            <React.Fragment>
              {/* <Card>
                <EffortContestKeywords />
              </Card> */}
              <KeywordManagement
                minStartDate={info.start_date}
                maxEndDate={info.end_date}
                effortId={info.contest_hash ? null : id}
                contestId={info.contest_hash ? info.contest_hash.id : null}
                // showCreate={info.points_keyword_hash ? false : true}
                showModalCreate
                showCreate={false}
                additionalCreateData={{
                  location_id: info.location_id,
                  organization_id: info.organization_id,
                }}
                locationId={info.location_id}
                organizationId={info.organization_id}
              />
            </React.Fragment>
          }
        />
        <Route
          path="awards"
          element={
            <React.Fragment>
              {info.variety === 5 ? (
                <MultiSweepsEffortAwards
                  effortName={info.title}
                  effortId={id}
                  locationId={info.location_id}
                  organizationId={info.organization_id}
                  fetchData={() => {
                    fetchAlerts();
                  }}
                  smsRequired={wizardSettings.keyword_sms_required}
                />
              ) : (
                <EffortAwards
                  awardType={info.award_type}
                  effortId={id}
                  endDate={info.end_date}
                  fetchEffort={fetchData}
                  pointSettings={info.points_settings}
                  pointsOnly={wizardSettings.point_award_required === true}
                  recurringEngagement={info.recurring_engagement}
                  showVarietyFilters={
                    hasWizardSettings && ![2, 3].includes(info.variety)
                  }
                  wizardSettings={wizardSettings}
                />
              )}
            </React.Fragment>
          }
        />
        {!isLocationManager && (
          <Route
            path="locations"
            element={
              <React.Fragment>
                <LocationAssociate
                  effortId={id}
                  disabledIds={info.active_location_ids}
                  selectReqData={{
                    status: 1,
                    campaign_effort_id: id,
                    organization_id: info.organization_id,
                    context: 'add_locations_to_effort',
                  }}
                  fetchData={fetchData}
                  bulkData={{
                    show_location_variety_filter: true,
                    download_request_data: {
                      // organization_id: info.organization_id,
                      context: 'campaign_effort_locations',
                      campaign_effort_id: id,
                    },
                    upload_request_data: {
                      campaign_effort_id: id,
                      context: 'add_effort_loc',
                    },
                  }}
                />
              </React.Fragment>
            }
          />
        )}
        <Route
          path="email"
          element={
            <React.Fragment>
              <EntityEmailTemplates
                postmarkTemplateNames={info.postmark_template_names}
                effortId={id}
                templates={[
                  {
                    name: 'Verify Email',
                    field_name: 'verify-email',
                    value: info.verification_template,
                  },
                  {
                    name: 'Registration Email',
                    field_name: 'registration',
                    value: info.registration_template,
                  },
                ]}
                fetchData={fetchData}
              />
            </React.Fragment>
          }
        />
        {(wizardSettings.microsite !== false ||
          isRideChallenge ||
          isVehicleDataEffort) && (
          <Route
            path="config"
            element={
              <Grid>
                <Grid.Col span={{ base: 12, md: 9 }}>
                  <div>
                    <MicrositeSettings
                      // campaignId={info.campaign_id}
                      isRideChallenge={isRideChallenge}
                      isVehicleDataEffort={isVehicleDataEffort}
                      effortId={id}
                      locationId={info.location_id}
                      onUpdate={() => {
                        fetchCheckupAndAlerts();
                      }}
                    />
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <EffortStatus
                    effortId={id}
                    fetchData={() => {
                      // fetchData();
                      fetchAlerts();
                    }}
                  />
                </Grid.Col>
              </Grid>
            }
          />
        )}
        {info.location_id && (
          <Route
            path="geospots"
            element={
              <React.Fragment>
                <GeospotAssignment
                  effortId={id}
                  locationId={info.location_id}
                />
              </React.Fragment>
            }
          />
        )}
        <Route
          path="addons"
          element={
            <React.Fragment>
              <EffortAddonManagement effortId={id} />
            </React.Fragment>
          }
        />
        <Route
          path="settings"
          element={
            <div>
              <Card>
                <EffortSettings
                  beginDate={info.start_date}
                  effortId={id}
                  endDate={info.end_date}
                  fetchEffort={fetchData}
                  wizardSettings={wizardSettings}
                  isRideChallenge={isRideChallenge}
                  isVehicleDataEffort={isVehicleDataEffort}
                />
              </Card>
            </div>
          }
        />
        {(isRideChallenge || isVehicleDataEffort) && !isManager && (
          <Route
            path="verification"
            element={
              <RideChallengeVerificationCodes
                effortId={id}
                effortName={info.title}
                fetchAlerts={() =>
                  queryClient.invalidateQueries([`efforts${id}ConfigAlerts`])
                }
              />
            }
          />
        )}
        {(isRideChallenge || isVehicleDataEffort) &&
          [
            <Route
              path="ride-challenge-management"
              key="ride-challenge-management"
              element={
                <div>
                  <RideChallengeManagement
                    effortId={id}
                    locationId={info.location_id}
                    isVehicleDataEffort={isVehicleDataEffort}
                  />
                </div>
              }
            />,
            <Route
              path="milestones"
              key="milestones"
              element={
                <div>
                  <MilestoneManagement effortId={id} />
                </div>
              }
            />,
          ].filter((f) => (!isRideChallenge ? f.key !== 'milestones' : true))}
        {!isManager && wizardSettings?.show_look_feel === true && (
          <Route
            path="paid-advertising"
            element={
              <div>
                <EntityActionAssociator effortId={id} />
              </div>
            }
          />
        )}
        {showInteraction && (
          <Route
            path="interaction"
            element={
              <React.Fragment>
                <UserInteractionTable
                  isRideChallenge={isRideChallenge}
                  useCheckins={wizardSettings.use_checkins}
                  useKeywords={wizardSettings.use_keywords}
                  reqData={{
                    campaign_effort_id: id,
                  }}
                />
              </React.Fragment>
            }
          />
        )}
        {!isManager && [
          <Route
            path="milestones"
            key="milestones"
            element={
              <div>
                <MilestoneManagement effortId={id} />
              </div>
            }
          />,
        ]}
        {!isManager && (
          <Route
            path="sms-messaging"
            element={
              <>
                <EffortSms info={info} fetchData={fetchData} effortId={id} />
              </>
            }
          />
        )}
        {/* <Route
          path="bulk-communication"
          element={
            <BulkCommunicationManagement
              effortId={id}
              locationId={info.location_id}
              orgId={info.organization_id}
            />
          }
        /> */}
      </Routes>
    </React.Fragment>
  );
}

export function Edit() {
  return <EffortForm onSuccess={fetchData} id={id} title={info.title} />;
}

const WizardToggle = ({ showWizard, setShowWizard }) => (
  <React.Fragment>
    <Space mt="sm" />
    <Switch
      label="Quick Settings"
      checked={showWizard}
      onChange={() => setShowWizard(!showWizard)}
      mb="lg"
    />
  </React.Fragment>
);

const EffortTitle = ({ info, id, fetchData, fetchCheckupAndAlerts }) => {
  const managerInfo = useSelector((state) => state.manager);
  const wizardSettings = info.wizard_settings;
  const showMicrosite =
    info.microsite_url && ![6, 7].includes(info.variety) ? true : false;
  const showSubnav =
    (info.microsite_url && showMicrosite) ||
    (!managerInfo && wizardSettings.show_csv !== false);

  return (
    <React.Fragment>
      <PageTitle
        title={info.title}
        subtitle="Campaign Effort"
        icon={entityIcon.effort()}
        iconColor={entityColor.effort}
        style={{ marginBottom: '0' }}
      >
        {showSubnav && (
          <Flex gap="sm">
            {info.microsite_url && showMicrosite && (
              <ThemeIcon
                component="a"
                radius="xl"
                color="gray"
                size="lg"
                target="_blank"
                href={`${import.meta.env.VITE_DOMAIN}/entry/${
                  info.microsite_url
                }`}
                variant="light"
                title="Preview Effort"
              >
                <IconExternalLink size={16} />
              </ThemeIcon>
            )}
            {!managerInfo && wizardSettings.show_csv !== false && (
              <EffortCsvDownload effortId={id} />
            )}
          </Flex>
        )}
      </PageTitle>
      <EntitySubtitles
        info={{
          ...info,
          campaign_id: null,
        }}
      />
      {info.points_keyword_hash && (
        <Flex>
          <ThemeIcon
            mr="xs"
            size="sm"
            variant="light"
            color={entityColor.keyword}
          >
            {entityIcon.keyword()}
          </ThemeIcon>
          <Text size="sm">
            <Anchor
              component={Link}
              target="_blank"
              to={`/keywords/${info.points_keyword_hash.id}`}
              style={{
                color: 'inherit',
              }}
            >
              {info.points_keyword_hash.name}
            </Anchor>
          </Text>
        </Flex>
      )}
      {info.contest_hash && (
        <Flex>
          <ThemeIcon
            mr="xs"
            size="sm"
            variant="light"
            color={entityColor.contest}
          >
            {entityIcon.contest()}
          </ThemeIcon>
          <Text size="sm">
            <Anchor
              component={Link}
              target="_blank"
              to={`/contests/${info.contest_hash.id}`}
              style={{
                color: 'inherit',
              }}
            >
              {info.contest_hash.name}
            </Anchor>
          </Text>
        </Flex>
      )}
      {info.edit && (
        <EffortCampaignManagement
          campaignId={info.campaign_id}
          campaignName={info.campaign_name}
          effortId={id}
          fetchData={() => {
            fetchData();
            fetchCheckupAndAlerts();
          }}
        />
      )}
    </React.Fragment>
  );
};

const EffortSms = ({ info, fetchData, effortId }) => {
  const entityReqData = {
    location_id: info.location_id,
    organization_id: info.organization_id,
    campaign_effort_id: effortId,
    location_name: info.location_name,
    organization_name: info.organization_name,
  };

  const smsInfo = info.sms_info;

  const items = [
    {
      title: 'SMS Marketing',
      context: 'sms_marketing',
      sms_info_key: 'sms_marketing_info',
      allowed: info.sms_marketing_allowed,
      ready: smsInfo?.sms_marketing_ready,
      active: smsInfo?.sms_marketing_active,
      reqData: entityReqData,
    },
    {
      title: 'Double Opt-In',
      context: 'sms_double_opt_in',
      sms_info_key: 'sms_double_opt_in_info',
      allowed: info.sms_marketing_allowed,
      ready: smsInfo?.sms_double_opt_in_ready,
      active: smsInfo?.sms_double_opt_in_active,
      reqData: entityReqData,
    },
  ].map((m) => ({
    ...m,
    messagingService: smsInfo?.[m.sms_info_key]?.messaging_service_ident
      ? smsInfo?.[m.sms_info_key]
      : null,
    sms: smsInfo?.[m.sms_info_key]?.phone_id ? smsInfo?.[m.sms_info_key] : null,
  }));

  return (
    <div>
      {items.map((m, i) => (
        <Box key={i} mt="lg">
          <EntityMessagingSms fetchData={fetchData} {...m} />
        </Box>
      ))}
    </div>
  );
};

const EffortTabs = ({
  info,
  id,
  fetchData,
  showTabList,
  isRideChallenge,
  isVehicleDataEffort,
  readingGoalDashboardCode,
  affiliateDashboardCode,
}) => {
  const managerInfo = useSelector((state) => state.manager);
  const isLocationManager =
    managerInfo && managerInfo.location_id ? true : false;
  const isLocationEffort = info.location_id ? true : false;
  const isOrgManager =
    managerInfo && managerInfo.organization_id ? true : false;
  const wizardSettings = info.wizard_settings;
  const qrCodeUrl =
    isRideChallenge || isVehicleDataEffort
      ? `${rideChallengePath}/${info.microsite_url}`
      : `${import.meta.env.VITE_DOMAIN}/entry/${info.microsite_url}`;

  return (
    <Tabs defaultValue="form">
      {showTabList && (
        <Tabs.List mb="sm">
          <Tabs.Tab
            leftSection={
              <IconForms style={{ width: rem(16), height: rem(16) }} />
            }
            value="form"
          >
            Form
          </Tabs.Tab>
          <Tabs.Tab
            leftSection={
              <IconQrcode style={{ width: rem(16), height: rem(16) }} />
            }
            value="qr"
          >
            {isRideChallenge || isVehicleDataEffort
              ? `Location QR Codes`
              : 'QR Code'}
          </Tabs.Tab>
          {isRideChallenge && (
            <Tabs.Tab
              leftSection={
                <IconQrcode style={{ width: rem(16), height: rem(16) }} />
              }
              value="qrDashboard"
            >
              Ride Challenge Dashboard
            </Tabs.Tab>
          )}
          {readingGoalDashboardCode && (
            <Tabs.Tab
              leftSection={
                <IconQrcode style={{ width: rem(16), height: rem(16) }} />
              }
              value="orgQr"
            >
              Reading Goal Dashboard
            </Tabs.Tab>
          )}
          {affiliateDashboardCode && (
            <Tabs.Tab
              leftSection={
                <IconQrcode style={{ width: rem(16), height: rem(16) }} />
              }
              value="affiliateQr"
            >
              Affiliate Dashboard
            </Tabs.Tab>
          )}
        </Tabs.List>
      )}
      <Tabs.Panel value="form">
        <div>
          <EffortForm
            allowManagerEdit={info.allow_manager_edit}
            apiAppEnum={info.api_app_enum}
            apiContext={info.api_context}
            apiProgram={info.api_program}
            awardType={info.award_type}
            endDate={info.end_date}
            endTime={info.end_time}
            id={id}
            micrositeGallery={info.microsite_gallery}
            internalName={info.internal_name}
            onSuccess={fetchData}
            pixelCode={info.pixel_code}
            recurringEngagement={info.recurring_engagement}
            sendFromEmail={info.send_from_email}
            sendRegistrationEmail={info.send_registration_email}
            shareData={info.share_data}
            showShareData={
              !isLocationManager && info.organization_id ? true : false
            }
            startDate={info.start_date}
            startTime={info.start_time}
            timezone={info.timezone}
            title={info.title}
            thirdPartyApiEnabled={info.third_party_api_enabled}
            thirdPartyCampaignCode={info.third_party_campaign_code}
            variety={info.variety}
            wizardSettings={wizardSettings}
          />
          {info.recurring_engagement !== effortRecurringEngagement.false &&
            !isVehicleDataEffort && (
              <React.Fragment>
                <Divider my="sm" />
                <EntityConfigSettings
                  effortId={id}
                  useKeywords={info.use_keywords}
                  useCheckins={info.use_checkins}
                  onUpdate={fetchData}
                />
              </React.Fragment>
            )}
          {isRideChallenge && (
            <>
              <Divider mt="lg" mb="lg" />
              <EffortPublicDashboardSettings effortId={id} />
            </>
          )}
        </div>
      </Tabs.Panel>
      <Tabs.Panel value="qr">
        {isLocationEffort ? (
          <QrCodeGenerator text={qrCodeUrl} />
        ) : (
          <>
            {isOrgManager || !managerInfo ? (
              <>
                <EffortLocationJoinList
                  effortId={id}
                  isRideChallenge={isRideChallenge}
                  isVehicleDataEffort={isVehicleDataEffort}
                />
              </>
            ) : (
              <>
                <QrCodeGenerator text={qrCodeUrl} />
              </>
            )}
          </>
        )}
        {/* {info.microsite_gallery ? (
      <>
        {(isOrgManager || !managerInfo) && (
          <Text fw={600} color="red" mb="sm">
            *The QR code below will lead to the Gallery, not
            the Effort.
          </Text>
        )}
        <QrCodeGenerator text={qrCodeUrl} />
      </>
    ) : (
      <Text>
        This effort will not show up in the gallery.
      </Text>
    )} */}
      </Tabs.Panel>
      <Tabs.Panel value="qrDashboard">
        <QrCodeGenerator text={getDashboardCode(info.dashboard_code)} />
        {isRideChallenge && !managerInfo && (
          <>
            <Divider my="lg" />
            <Box>
              <Text fw={600} mb="xs">
                Generate Dashboards
              </Text>
              <Flex
                gap="xs"
                direction={{ base: 'column', md: 'row' }}
                wrap="wrap"
              >
                {[
                  {
                    text: 'Org Rollup',
                    reqData: {
                      campaign_effort_id: id,
                      context: 'ride_challenge',
                      entity: 'owner',
                    },
                  },
                  {
                    text: 'Locations Rollup',
                    reqData: {
                      campaign_effort_id: id,
                      context: 'ride_challenge',
                      entity: 'locations',
                    },
                  },
                  {
                    text: 'Readings Goal',
                    reqData: {
                      campaign_effort_id: id,
                      context: 'rc_readings_goal',
                      entity: 'owner',
                    },
                  },
                  {
                    text: 'HOG Chapter Rollup',
                    reqData: {
                      campaign_effort_id: id,
                      context: 'rc_hog_chapter_rollup',
                      entity: 'owner',
                    },
                  },
                ].map((item, index) => (
                  <EffortGenerateDashboardButton
                    key={index}
                    text={item.text}
                    reqData={{
                      ...item.reqData,
                      manual: true,
                    }}
                  />
                ))}
              </Flex>
            </Box>
          </>
        )}
      </Tabs.Panel>
      {readingGoalDashboardCode && (
        <Tabs.Panel value="orgQr">
          <Card>
            <QrCodeGenerator
              text={getDashboardCode(readingGoalDashboardCode)}
            />
          </Card>
        </Tabs.Panel>
      )}
      {affiliateDashboardCode && (
        <Tabs.Panel value="affiliateQr">
          <QrCodeGenerator text={getDashboardCode(affiliateDashboardCode)} />
        </Tabs.Panel>
      )}
    </Tabs>
  );
};
